import React, { useState } from "react";

import {
  FlexComp,
  FormattedText,
  ImageComponent,
  ParaTag,
  SpanTag,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";

import {
  Card,
  CardCtaButtonList,
  CardCtaDivider,
  CardPadding,
  InfoStrip,
  ListBox,
  ListRow,
  RowMeta,
  SectionHeaderIcon,
} from "../../OrderDetailV2.style";
import { useOrderDetailV2 } from "../../OrderDetailV2Context";
import type { OrderDetailV2TravellersProps, Traveller } from "../../types";
import { EditTravellersModal } from "../EditTravellersModal";
import { OrderDetailV2Tooltip } from "../OrderDetailV2Tooltip";
import { TextButton } from "../TextButton";

export const OrderDetailV2Travellers: React.FC<
  OrderDetailV2TravellersProps
> = ({
  icon,
  title,
  actionLabel,
  isActionDisabled,
  actionDisabledReason,
  persuasionText,
  travellers,
  formConfig,
  modalConfig,
  entities = [],
  actionList = [],
  isActionVisible = true,
}: OrderDetailV2TravellersProps) => {
  const ctx = useOrderDetailV2();
  const { bookingId, refreshOrderDetails } = ctx;
  const disabled = Boolean(isActionDisabled);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const device = useBreakpointV2();

  const openTravellersModal = () => {
    if (!disabled && formConfig) {
      setIsModalOpen(true);
    }
  };

  return (
    <Card>
      <CardPadding>
        <FlexComp
          alignItems="center"
          justifyContent="space-between"
          margin="0 0 10px"
        >
          <FlexComp alignItems="center" gap="8px" margin="0">
            {device === "lg" && (
              <SectionHeaderIcon>
                <ImageComponent src={icon} width="20px" height="20px" />
              </SectionHeaderIcon>
            )}
            <SpanTag
              fontSize="large"
              fontWeight="bold"
              color={device === "lg" ? theme.greyText : theme.blackText}
            >
              <FormattedText
                value={{
                  text: {
                    value: title,
                    styles: {
                      mobile: {
                        "font-size": "16px", // Mobile font size
                      },
                    },
                  },
                }}
              />
            </SpanTag>
          </FlexComp>
          {device === "lg" && isActionVisible && (
            <OrderDetailV2Tooltip
              message={actionDisabledReason || ""}
              trigger="both"
              show={disabled}
              position="bottom"
              autoHideDelay={5000}
              disabled={!disabled || !actionDisabledReason}
            >
              <TextButton
                btnTxt={actionLabel}
                onClick={openTravellersModal}
                showDefaultRightIcon={false}
                disabled={disabled}
              />
            </OrderDetailV2Tooltip>
          )}
        </FlexComp>

        {persuasionText && device === "lg" ? (
          <InfoStrip>
            <SpanTag fontSize="xs" color={theme.greyText7}>
              <FormattedText value={persuasionText} />
            </SpanTag>
          </InfoStrip>
        ) : null}

        <ListBox>
          {travellers.map((t: Traveller, index: number) => {
            const { policyUrl, cta, title, desc } = t;
            const { label, icon, actionId, isEnabled, isVisible } = cta || {};

            const handleCtaClick = () => {
              if (policyUrl && isEnabled) {
                window.open(policyUrl, "_blank");
              }
            };
            return (
              <ListRow key={`${t?.id || "traveller"}-${index}`}>
                <FlexComp
                  alignItems="flex-start"
                  justifyContent="space-between"
                  margin="0"
                >
                  <div style={{ minWidth: 0 }}>
                    <ParaTag fontSize="md" fontWeight="bold">
                      <FormattedText
                        value={{
                          text: {
                            value: title,
                            styles: {
                              mobile: {
                                "font-size": "14px",
                              },
                            },
                          },
                        }}
                      />
                    </ParaTag>
                    {desc && (
                      <RowMeta>
                        <SpanTag fontSize="sm" color={theme.greyText}>
                          <FormattedText
                            value={{
                              text: {
                                value: desc,
                                styles: {
                                  mobile: {
                                    "font-size": "12px", // Mobile font size
                                  },
                                },
                              },
                            }}
                          />
                        </SpanTag>
                      </RowMeta>
                    )}
                  </div>

                  {isVisible && policyUrl && (
                    <TextButton
                      btnTxt={label}
                      leftIcon={icon}
                      disabled={!isEnabled}
                      onClick={handleCtaClick}
                      showDefaultRightIcon={false}
                    />
                  )}
                </FlexComp>
              </ListRow>
            );
          })}
        </ListBox>
      </CardPadding>

      {device === "sm" && (
        <>
          <CardCtaDivider />
          <CardCtaButtonList>
            {(actionList as Array<{ id: string; text: string }>).map(
              (action) => (
                <OrderDetailV2Tooltip
                  key={action.id}
                  message={actionDisabledReason || ""}
                  trigger="both"
                  show={disabled}
                  position="bottom"
                  autoHideDelay={5000}
                  disabled={!disabled || !actionDisabledReason}
                >
                  <TextButton
                    fullWidth={true}
                    btnTxt={action.text}
                    disabled={disabled}
                    onClick={openTravellersModal}
                  />
                </OrderDetailV2Tooltip>
              )
            )}
          </CardCtaButtonList>
        </>
      )}

      {formConfig && bookingId && (
        <EditTravellersModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          formConfig={formConfig}
          entities={entities}
          bookingId={bookingId}
          onSave={() => {}}
          onEndorsementSuccess={refreshOrderDetails}
          modalTitle={modalConfig?.header?.title || "Edit Travellers"}
          saveButtonLabel={
            modalConfig?.footer?.cta?.[0]?.label || "SAVE DETAILS"
          }
        />
      )}
    </Card>
  );
};
